import { Coffee, Search, Settings, Users, Sparkles, QrCode, ExternalLink, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoginArea } from "@/components/auth/LoginArea";
import { Link } from "react-router-dom";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAuthor } from "@/hooks/useAuthor";
import { useNostr } from "@nostrify/react";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useSeoMeta } from "@unhead/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { nip19 } from "nostr-tools";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useToast } from "@/hooks/useToast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import type { NostrEvent, NostrMetadata } from "@nostrify/nostrify";
import { ShareDropdown } from "@/components/ShareDropdown";
import { QRCodeDialog } from "@/components/QRCodeDialog";
import { profileSearchIndex } from "@/lib/profileSearchIndex";

const PUBKEYS_PER_BATCH = 40;

export default function Index() {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const [followingSearch, setFollowingSearch] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isQROpen, setIsQROpen] = useState(false);

  const author = useAuthor(user?.pubkey || "");
  const metadata: NostrMetadata | undefined = author.data?.metadata;

  useSeoMeta({
    title: "ZapMeACoffee - Lightning Tips for Creators",
    description: "Accept Lightning tips from your supporters. No middlemen, no fees.",
  });

  // Get following list
  const { data: following } = useQuery({
    queryKey: ["following", user?.pubkey],
    queryFn: async (c) => {
      if (!user?.pubkey) return [];
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);
      const events = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal }
      );
      if (events.length === 0) return [];
      const follows = events[0].tags
        .filter((t) => t[0] === "p")
        .map((t) => t[1]);
      return follows;
    },
    enabled: !!user?.pubkey,
    staleTime: 5 * 60 * 1000, // Keep fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });

  // Infinite scroll query for followed profiles with Lightning addresses
  const {
    data: followedProfilesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: loadingFollowing,
  } = useInfiniteQuery({
    queryKey: ["followed-profiles-infinite", following],
    queryFn: async ({ pageParam = 0, signal }) => {
      if (!following || following.length === 0) return { profiles: [], nextPage: null };

      const start = pageParam * PUBKEYS_PER_BATCH;
      const end = start + PUBKEYS_PER_BATCH;
      const batchPubkeys = following.slice(start, end);

      if (batchPubkeys.length === 0) return { profiles: [], nextPage: null };

      const querySignal = AbortSignal.any([signal, AbortSignal.timeout(8000)]);
      const events = await nostr.query(
        [{ kinds: [0], authors: batchPubkeys }],
        { signal: querySignal }
      );

      const profiles = events
        .map((event) => {
          try {
            const meta: NostrMetadata = JSON.parse(event.content);
            // Add to search index
            profileSearchIndex.add(event.pubkey, meta);
            return { event, metadata: meta, pubkey: event.pubkey };
          } catch {
            return null;
          }
        })
        .filter((item): item is { event: NostrEvent; metadata: NostrMetadata; pubkey: string } =>
          item !== null && !!item.metadata.lud16
        );

      const hasMore = end < following.length;
      return { profiles, nextPage: hasMore ? pageParam + 1 : null };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!following && following.length > 0,
    staleTime: 10 * 60 * 1000, // Keep fresh for 10 minutes (profiles don't change often)
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });

  // Flatten all pages into a single array and deduplicate by pubkey
  const allFollowedProfiles = useMemo(() => {
    if (!followedProfilesData) return [];
    const seen = new Set<string>();
    return followedProfilesData.pages.flatMap(page => page.profiles).filter(profile => {
      if (seen.has(profile.pubkey)) return false;
      seen.add(profile.pubkey);
      return true;
    });
  }, [followedProfilesData]);

  // Filter profiles based on search
  const filteredProfiles = useMemo(() => {
    if (!followingSearch.trim()) return allFollowedProfiles;
    const searchLower = followingSearch.toLowerCase();
    return allFollowedProfiles.filter(({ metadata: meta }) => {
      const name = (meta.name || "").toLowerCase();
      const displayName = (meta.display_name || "").toLowerCase();
      const nip05 = (meta.nip05 || "").toLowerCase();
      return name.includes(searchLower) || displayName.includes(searchLower) || nip05.includes(searchLower);
    });
  }, [allFollowedProfiles, followingSearch]);

  // Intersection Observer for infinite scroll using callback ref
  const loadMoreCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasNextPage && !isFetchingNextPage && !followingSearch.trim()) {
            fetchNextPage();
          }
        },
        {
          root: null,
          rootMargin: "200px",
          threshold: 0,
        }
      );

      observer.observe(node);

      // Store observer reference on the node for cleanup
      (node as HTMLDivElement & { _observer?: IntersectionObserver })._observer?.disconnect();
      (node as HTMLDivElement & { _observer?: IntersectionObserver })._observer = observer;
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, followingSearch]
  );

  const displayName = metadata?.display_name || metadata?.name || "Anonymous";
  const userNpub = user?.pubkey ? nip19.npubEncode(user.pubkey) : "";
  const profileUrl = userNpub ? `https://zapmeacoffee.com/${userNpub}` : "";
  const followingCount = following?.length || 0;

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="lg:max-w-[60%] mx-auto">
        {user ? (
          <Tabs defaultValue="my-page" className="space-y-8">
            <TabsList className="w-full h-12 p-1 bg-secondary/50 rounded-xl">
              <TabsTrigger
                value="my-page"
                className="flex-1 h-full rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm text-sm font-medium transition-all"
              >
                My Page
              </TabsTrigger>
              <TabsTrigger
                value="following"
                className="flex-1 h-full rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm text-sm font-medium transition-all"
              >
                <Users className="h-4 w-4 mr-2" />
                Following ({followingCount})
              </TabsTrigger>
            </TabsList>

            {/* My Page Tab */}
            <TabsContent value="my-page" className="space-y-6 mt-6">
              <Card className="overflow-hidden border-0 shadow-xl shadow-black/5 rounded-3xl">
                <CardContent className="p-8">
                  {/* Profile */}
                  <div className="flex items-start gap-5 mb-5">
                    <Avatar className="h-20 w-20 ring-2 ring-border/50 shadow-lg flex-shrink-0">
                      <AvatarImage src={metadata?.picture} className="object-cover" />
                      <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary text-2xl font-semibold">
                        {displayName[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-2xl font-semibold tracking-tight truncate">{displayName}</h2>
                      {metadata?.nip05 && (
                        <p className="text-sm text-muted-foreground truncate">{metadata.nip05}</p>
                      )}
                    </div>
                    {/* QR and Settings buttons - top right */}
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        variant="secondary"
                        size="icon"
                        onClick={() => setIsQROpen(true)}
                        className="h-10 w-10 rounded-xl"
                      >
                        <QrCode className="h-4 w-4" />
                      </Button>
                      <EditProfileDialog
                        open={isEditOpen}
                        onOpenChange={setIsEditOpen}
                        metadata={metadata}
                      />
                    </div>
                  </div>

                  {metadata?.about && (
                    <p className="text-muted-foreground mb-8 whitespace-pre-wrap leading-relaxed">{metadata.about}</p>
                  )}

                  {/* Actions - View Page and Share side by side */}
                  <div className="flex gap-3">
                    <Button
                      variant="secondary"
                      asChild
                      className="flex-1 h-12 rounded-2xl"
                    >
                      <a
                        href={`https://zapmeacoffee.com/${userNpub}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Page
                      </a>
                    </Button>

                    <ShareDropdown
                      url={profileUrl}
                      title="Check out my ZapMeACoffee page!"
                      description={`Support me with Lightning tips! ⚡ ${displayName}`}
                      className="flex-1 h-12 rounded-2xl bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
                    />
                  </div>

                  {/* QR Code Dialog */}
                  <QRCodeDialog
                    open={isQROpen}
                    onOpenChange={setIsQROpen}
                    url={profileUrl}
                    title="Scan to open my page"
                  />

                  {/* Lightning Address Status */}
                  {!metadata?.lud16 && (
                    <div className="mt-6 p-5 bg-accent rounded-2xl">
                      <p className="text-sm text-accent-foreground">
                        ⚡ Add a Lightning address to start receiving tips!{" "}
                        <button
                          onClick={() => setIsEditOpen(true)}
                          className="font-medium underline underline-offset-2 hover:no-underline"
                        >
                          Edit profile
                        </button>
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Following Tab */}
            <TabsContent value="following" className="space-y-4 mt-6">
              {/* Search within following */}
              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search your following..."
                  className="pl-11 h-11 bg-secondary/50 border-0 rounded-xl focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0"
                  value={followingSearch}
                  onChange={(e) => setFollowingSearch(e.target.value)}
                />
              </div>

              {/* Progress indicator */}
              {allFollowedProfiles.length > 0 && (
                <p className="text-sm text-muted-foreground mb-2">
                  Showing {filteredProfiles.length} creator{filteredProfiles.length !== 1 ? 's' : ''} with Lightning addresses
                  {hasNextPage && !followingSearch.trim() && ' • Scroll for more'}
                </p>
              )}

              {loadingFollowing ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <Card key={i} className="border-0 shadow-lg shadow-black/5 rounded-2xl">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-4">
                          <Skeleton className="h-14 w-14 rounded-2xl" />
                          <div className="flex-1 space-y-2 min-w-0">
                            <Skeleton className="h-5 w-32" />
                            <Skeleton className="h-4 w-48" />
                          </div>
                          <Skeleton className="h-10 w-24 rounded-xl" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : filteredProfiles.length > 0 ? (
                <div className="space-y-4">
                  {filteredProfiles.map(({ pubkey, metadata: meta }) => (
                    <CreatorCard key={pubkey} pubkey={pubkey} metadata={meta} />
                  ))}

                  {/* Load more trigger */}
                  {!followingSearch.trim() && hasNextPage && (
                    <div ref={loadMoreCallbackRef} className="py-4 flex flex-col items-center gap-4">
                      {isFetchingNextPage ? (
                        <div className="space-y-4 w-full">
                          {[...Array(2)].map((_, i) => (
                            <Card key={i} className="border-0 shadow-lg shadow-black/5 rounded-2xl">
                              <CardContent className="p-5">
                                <div className="flex items-center gap-4">
                                  <Skeleton className="h-14 w-14 rounded-2xl" />
                                  <div className="flex-1 space-y-2 min-w-0">
                                    <Skeleton className="h-5 w-32" />
                                    <Skeleton className="h-4 w-48" />
                                  </div>
                                  <Skeleton className="h-10 w-24 rounded-xl" />
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={() => fetchNextPage()}
                          className="rounded-xl"
                        >
                          Load More Creators
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Card className="border border-dashed border-border/60 shadow-none rounded-2xl">
                  <CardContent className="py-16 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary mb-5">
                      <Users className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="text-foreground font-medium mb-2">
                      {followingSearch.trim()
                        ? "No matching creators found"
                        : following?.length === 0
                          ? "You're not following anyone yet"
                          : "No creators with Lightning addresses"
                      }
                    </p>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                      {followingSearch.trim()
                        ? "Try a different search term"
                        : "Search for creators to support them with coffee tips!"
                      }
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          /* Logged Out State */
          <Card className="border-0 shadow-2xl shadow-black/10 rounded-3xl overflow-hidden">
            <CardContent className="p-0">
              {/* Decorative header */}
              <div className="relative h-32 bg-gradient-to-br from-primary/10 via-accent to-secondary overflow-hidden">
                <div className="absolute top-8 right-8 w-40 h-40 bg-primary/20 rounded-full blur-3xl" />
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-primary/30 rounded-full blur-2xl" />
                <Sparkles className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-8 text-primary/40" />
              </div>

              <div className="px-8 pb-12 pt-8 text-center">
                <div className="inline-flex items-center justify-center mb-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-2xl" />
                    <div className="relative bg-gradient-to-br from-primary to-primary/80 p-5 rounded-3xl shadow-xl shadow-primary/30">
                      <Zap className="h-10 w-10 text-primary-foreground fill-primary-foreground" />
                    </div>
                  </div>
                </div>
                <h1 className="text-3xl font-semibold tracking-tight mb-3">
                  Lightning Tips for Creators
                </h1>
                <p className="text-muted-foreground mb-10 max-w-md mx-auto leading-relaxed">
                  Accept Bitcoin tips from your supporters. No middlemen, no fees.
                  Just pure peer-to-peer value.
                </p>
                <LoginArea className="w-full justify-center" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function CreatorCard({ pubkey, metadata }: { pubkey: string; metadata: NostrMetadata }) {
  const npub = nip19.npubEncode(pubkey);
  const displayName = metadata.display_name || metadata.name || "Anonymous";
  const about = metadata.about || "";
  const truncatedAbout = about.length > 140 ? about.slice(0, 140).trim() + "..." : about;

  return (
    <Card className="border-0 shadow-lg shadow-black/5 hover:shadow-xl hover:shadow-black/10 transition-all rounded-2xl group">
      <CardContent className="p-5">
        <Link to={`/${npub}`} className="flex items-center gap-4">
          <Avatar className="h-14 w-14 rounded-2xl ring-2 ring-border/50">
            <AvatarImage src={metadata.picture} className="object-cover" />
            <AvatarFallback className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-semibold">
              {displayName[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">{displayName}</h3>
            {truncatedAbout && (
              <p className="text-sm text-muted-foreground line-clamp-2">{truncatedAbout}</p>
            )}
          </div>
          <Button
            size="sm"
            className="rounded-xl bg-primary hover:bg-primary/90 shadow-md shadow-primary/20 flex-shrink-0 opacity-90 group-hover:opacity-100 transition-all"
          >
            <Coffee className="h-4 w-4 mr-1.5" />
            Tip
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function EditProfileDialog({
  open,
  onOpenChange,
  metadata
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  metadata?: NostrMetadata;
}) {
  const { toast } = useToast();
  const { mutate: createEvent, isPending } = useNostrPublish();

  const [formData, setFormData] = useState({
    name: metadata?.name || "",
    display_name: metadata?.display_name || "",
    about: metadata?.about || "",
    picture: metadata?.picture || "",
    banner: metadata?.banner || "",
    lud16: metadata?.lud16 || "",
    nip05: metadata?.nip05 || "",
    website: metadata?.website || "",
  });

  // Update form when metadata changes
  useEffect(() => {
    if (metadata) {
      setFormData({
        name: metadata.name || "",
        display_name: metadata.display_name || "",
        about: metadata.about || "",
        picture: metadata.picture || "",
        banner: metadata.banner || "",
        lud16: metadata.lud16 || "",
        nip05: metadata.nip05 || "",
        website: metadata.website || "",
      });
    }
  }, [metadata]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    createEvent(
      {
        kind: 0,
        content: JSON.stringify({
          ...metadata,
          ...formData,
        }),
      },
      {
        onSuccess: () => {
          toast({ title: "Profile updated! ✨" });
          onOpenChange(false);
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update profile",
            variant: "destructive"
          });
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="icon" className="h-10 w-10 rounded-xl">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md rounded-3xl border-0 shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Edit Profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">Username</Label>
              <Input
                id="name"
                placeholder="satoshi"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-11 rounded-xl border-0 bg-secondary/50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="display_name" className="text-sm font-medium">Display Name</Label>
              <Input
                id="display_name"
                placeholder="Satoshi Nakamoto"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                className="h-11 rounded-xl border-0 bg-secondary/50"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="about" className="text-sm font-medium">Bio</Label>
            <Textarea
              id="about"
              placeholder="Tell the world about yourself..."
              value={formData.about}
              onChange={(e) => setFormData({ ...formData, about: e.target.value })}
              rows={3}
              className="rounded-xl border-0 bg-secondary/50 resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="picture" className="text-sm font-medium">Profile Picture URL</Label>
            <Input
              id="picture"
              type="url"
              placeholder="https://..."
              value={formData.picture}
              onChange={(e) => setFormData({ ...formData, picture: e.target.value })}
              className="h-11 rounded-xl border-0 bg-secondary/50"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lud16" className="text-sm font-medium">Lightning Address ⚡</Label>
            <Input
              id="lud16"
              type="email"
              placeholder="you@getalby.com"
              value={formData.lud16}
              onChange={(e) => setFormData({ ...formData, lud16: e.target.value })}
              className="h-11 rounded-xl border-0 bg-secondary/50"
            />
            <p className="text-xs text-muted-foreground">Required to receive tips</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website" className="text-sm font-medium">Website</Label>
            <Input
              id="website"
              type="url"
              placeholder="https://yoursite.com"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="h-11 rounded-xl border-0 bg-secondary/50"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12 rounded-2xl bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25"
            disabled={isPending}
          >
            {isPending ? "Saving..." : "Save Profile"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
